// @ts-nocheck
import translate from "@insite/client-framework/Translate";
import { ProductModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import capitalizeWords from "@store/Framework/Common/Utilities/capitalizeWords";
import getTrimTraitValueDisplayName from "@store/Framework/Common/Utilities/getTrimTraitValueDisplayName";
import { productCardProps } from "@store/Themes/common/props/productCard";
import useExpandProps, { ExpandStyles } from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { visuallyHiddenStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useMemo } from "react";
import { css } from "styled-components";

export interface VariantTraitValueViewModel {
    label: string;
    value: string;
}

export interface ProductVariantTraitValuesPresentationProps extends ExpandStyles<ProductVariantTraitValuesStyles> {}

export interface ProductVariantTraitValuesProps extends ProductVariantTraitValuesPresentationProps {
    product: ProductModel;
    variantTrait?: VariantTraitModel | null;
}

export interface ProductVariantTraitValuesStyles {
    label?: TypographyPresentationProps;
    separator?: TypographyPresentationProps;
    trait?: TypographyPresentationProps;
    value?: TypographyPresentationProps;
    wrapper?: TypographyPresentationProps;
}

export const productVariantTraitValuesStyles: ProductVariantTraitValuesStyles = {
    wrapper: {
        forwardAs: "ul",
        css: css`
            ${productCardProps.spacingWrapper?.css}
            font-weight: ${x => x.theme.design.typography.weight.base};
            font-size: ${x => x.theme.design.typography.size.smaller};
        `,
    },
    separator: {
        css: css`
            font-family: ${x => x.theme.design.typography.fontFamily.title};
            font-style: italic;
        `,
    },
    trait: {
        forwardAs: "li",
        css: css`
            display: inline;
        `,
    },
    label: {
        css: visuallyHiddenStyles(),
    },
    value: {
        css: css`
            font-family: ${x => x.theme.design.typography.fontFamily.title};
            font-style: italic;
        `,
    },
};

const ProductVariantTraitValues: FC<ProductVariantTraitValuesProps> = ({
    extendedStyles,
    overridedStyles,
    product,
    variantTrait,
}) => {
    const styles = useExpandProps(productVariantTraitValuesStyles, extendedStyles, overridedStyles);
    const { childTraitValues, variantTraits } = product;

    if (!variantTraits || !childTraitValues) {
        return null;
    }

    const variantTraitValues = useMemo(() => {
        return [...variantTraits]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .reduce((result, trait) => {
                if (variantTrait && trait.id === variantTrait.id) {
                    return result;
                }

                const value = childTraitValues.find(x => x.styleTraitId === trait.id);

                if (!value) {
                    return result;
                }

                const viewModel: VariantTraitValueViewModel = {
                    label: trait.nameDisplay,
                    value: capitalizeWords(getTrimTraitValueDisplayName(product, trait.name, value.valueDisplay)),
                };

                return [...result, viewModel];
            }, [] as VariantTraitValueViewModel[]);
    }, [childTraitValues, variantTraits, product]);

    if (!variantTraitValues.length) {
        return null;
    }

    return (
        <Typography {...styles.wrapper}>
            {variantTraitValues.map(({ label, value }, index) => (
                <Typography key={`${label}-${value}`} {...styles.trait}>
                    {index > 0 && (
                        <Typography {...styles.separator} aria-hidden="true">
                            &nbsp;/&nbsp;
                        </Typography>
                    )}
                    <Typography {...styles.label}>{translate(`{0}:`, label)}&nbsp;</Typography>
                    <Typography {...styles.value}>{value}</Typography>
                </Typography>
            ))}
        </Typography>
    );
};

export default ProductVariantTraitValues;
